import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useTutorial, TutorialStep } from "@/hooks/useTutorial";
import { 
  Search, 
  Copy, 
  History, 
  Brain, 
  FileText, 
  X, 
  ChevronRight,
  MessageCircle
} from "lucide-react";

const stepConfig: Record<TutorialStep, { 
  icon: React.ReactNode; 
  title: string; 
  description: string;
  hint: string;
}> = {
  first_search: {
    icon: <Search className="h-5 w-5" />,
    title: "Hacé tu primera búsqueda",
    description: 'Buscá "responsabilidad civil de una clínica de fertilidad por no indicar diagnóstico genético"',
    hint: 'Seleccioná "Cámara Nacional Civil" y hacé clic en Buscar',
  },
  copy_citation: {
    icon: <Copy className="h-5 w-5" />,
    title: "Copiá una cita",
    description: "Usá el botón 'Copiar cita' en cualquier resultado",
    hint: "La cita se copia al portapapeles lista para pegar",
  },
  second_search: {
    icon: <Search className="h-5 w-5" />,
    title: "Hacé una pregunta directa",
    description: '¿Corresponde indexar una indemnización laboral?',
    hint: 'Usá "Cámara Nacional del Trabajo"',
  },
  view_history: {
    icon: <History className="h-5 w-5" />,
    title: "Visitá tu historial",
    description: "Todas tus búsquedas quedan guardadas",
    hint: "Hacé clic en 'Historial' en el menú",
  },
  generate_summary: {
    icon: <Brain className="h-5 w-5" />,
    title: "Generá un resumen con IA",
    description: "Hacé clic en 'Resumir con IA' en cualquier fallo",
    hint: "La IA resume el fallo en segundos",
  },
  word_addin: {
    icon: <FileText className="h-5 w-5" />,
    title: "Instalá el Add-in de Word",
    description: "Buscá jurisprudencia sin salir de Word",
    hint: "Disponible en Microsoft Store",
  },
};

export function TutorialOverlay() {
  const { 
    currentStep, 
    completedStepsCount, 
    totalSteps, 
    dismissTutorial,
    isLoading,
    progress
  } = useTutorial();
  const [isMinimized, setIsMinimized] = useState(false);

  // Don't show if loading, no current step, or tutorial completed/dismissed
  if (isLoading || !currentStep || progress?.tutorial_completed || progress?.tutorial_dismissed) {
    return null;
  }

  const config = stepConfig[currentStep];
  const progressPercent = (completedStepsCount / totalSteps) * 100;
  const stepNumber = completedStepsCount + 1;

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
        <Button
          onClick={() => setIsMinimized(false)}
          className="rounded-full h-14 w-14 shadow-lg bg-primary hover:bg-primary/90"
        >
          <span className="text-lg font-bold">{stepNumber}</span>
          <span className="text-xs">/{totalSteps}</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <Card className="w-80 shadow-xl border-primary/20 bg-card/95 backdrop-blur-sm">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                {config.icon}
              </div>
              <span className="text-xs font-medium text-muted-foreground">
                Paso {stepNumber} de {totalSteps}
              </span>
            </div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsMinimized(true)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                onClick={dismissTutorial}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <Progress value={progressPercent} className="h-1 mt-2" />
        </CardHeader>

        <CardContent className="px-4 py-3">
          <h3 className="font-semibold text-foreground mb-1">{config.title}</h3>
          <p className="text-sm text-muted-foreground mb-2">{config.description}</p>
          <p className="text-xs text-primary/80 italic">💡 {config.hint}</p>
        </CardContent>

        <CardFooter className="px-4 pb-4 pt-0 flex justify-between items-center">
          <Button
            variant="link"
            size="sm"
            className="text-xs text-muted-foreground p-0 h-auto"
            onClick={dismissTutorial}
          >
            Omitir tutorial
          </Button>
          <a
            href="https://wa.me/5491155698892?text=Tengo%20una%20duda%20sobre%20DoctIA"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="ghost" size="sm" className="text-xs gap-1">
              <MessageCircle className="h-3 w-3" />
              Ayuda
            </Button>
          </a>
        </CardFooter>
      </Card>
    </div>
  );
}
